<template>
  <div class="batch-recharge">
    <div class="ios-group-header">批量充值</div>

    <template v-if="step === 0">
      <div class="ios-card batch-panel">
        <div class="batch-head">
          <div class="batch-title-wrap">
            <div class="batch-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="5" width="18" height="14" rx="3"></rect>
                <path d="M7 9h10"></path>
                <path d="M7 13h5"></path>
              </svg>
            </div>
            <div>
            <div class="panel-title">校验卡密和 Token</div>
            <div class="panel-desc">最多添加 5 条任务，全部校验成功后进入确认提交。</div>
            </div>
          </div>
          <button
            type="button"
            class="btn-tinted compact-button"
            :disabled="rows.length >= maxRows || validating"
            @click="addRow"
          >
            + 添加
          </button>
        </div>

        <div class="batch-list">
          <div
            v-for="(row, index) in rows"
            :key="row.id"
            class="batch-item"
            :class="{ 'has-error': row.cardError || row.tokenError }"
          >
            <div class="batch-item-head">
              <div class="task-title">
                <span>{{ index + 1 }}</span>
                <strong>充值任务</strong>
              </div>
              <div class="batch-item-actions">
                <span :class="getValidateStatusClass(row)">{{ getValidateStatusText(row) }}</span>
                <button
                  v-if="rows.length > 1"
                  type="button"
                  class="link-button danger"
                  :disabled="validating"
                  @click="removeRow(index)"
                >
                  删除
                </button>
              </div>
            </div>

            <div class="field-grid">
              <div class="input-group">
                <label class="ios-field-label">卡密编号</label>
                <input
                  v-model.trim="row.cardCode"
                  class="ios-input"
                  :class="{ 'has-error': row.cardError }"
                  placeholder="例如：CARD-123456"
                  :disabled="validating"
                  @input="clearRowValidation(row)"
                />
                <div v-if="row.cardError" class="ios-error-text">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{{ row.cardError }}</span>
                </div>
                <div v-else-if="row.cardInfo" class="field-success">
                  {{ row.cardInfo.productName }} · {{ row.cardInfo.cardStatusText }}
                </div>
              </div>

              <div class="input-group">
                <label class="ios-field-label">Token 信息</label>
                <textarea
                  v-model="row.token"
                  class="ios-textarea token-input"
                  :class="{ 'has-error': row.tokenError }"
                  rows="3"
                  placeholder='{"user":{"email":"test@example.com"}}'
                  :disabled="validating"
                  @input="clearRowValidation(row)"
                ></textarea>
                <div v-if="row.tokenError" class="ios-error-text">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{{ row.tokenError }}</span>
                </div>
                <div v-else-if="row.tokenInfo" class="field-success">
                  {{ row.tokenInfo.email || '账号已校验' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="error" class="ios-callout is-danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ error }}</span>
        </div>

        <button
          type="button"
          class="btn-filled"
          :disabled="!canValidate || validating"
          @click="validateAll"
        >
          <span v-if="validating" class="ios-spinner"></span>
          {{ validating ? '校验中…' : '校验全部任务' }}
        </button>
      </div>
    </template>

    <template v-else-if="step === 1">
      <div class="ios-card batch-panel">
        <div class="batch-head">
          <div>
            <div class="panel-title">确认提交</div>
            <div class="panel-desc">已通过 {{ validCount }} 条任务校验，确认后将逐条创建订单。</div>
          </div>
        </div>

        <div class="ios-list confirm-list">
          <div v-for="(row, index) in rows" :key="row.id" class="ios-row confirm-row">
            <div>
              <span class="confirm-index">任务 {{ index + 1 }}</span>
              <strong>{{ row.cardInfo.productName }}</strong>
              <small>{{ row.cardCode }}</small>
            </div>
            <span class="ios-row-value">{{ row.tokenInfo.email || row.tokenInfo.name || '已校验账号' }}</span>
          </div>
        </div>

        <div v-if="error" class="ios-callout is-danger">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ error }}</span>
        </div>

        <div class="button-group">
          <button type="button" class="btn-gray" :disabled="submitting" @click="step = 0">
            返回修改
          </button>
          <button type="button" class="btn-filled btn-destructive" :disabled="submitting" @click="submitAll">
            <span v-if="submitting" class="ios-spinner"></span>
            {{ submitting ? '提交中…' : '确认提交' }}
          </button>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="ios-card batch-panel">
        <div class="batch-head">
          <div>
            <div class="panel-title">批量任务进度</div>
            <div class="panel-desc">每个订单会独立轮询结果，完成、异常或退卡后停止。</div>
          </div>
          <button type="button" class="btn-tinted compact-button" @click="resetBatch">
            新建批量
          </button>
        </div>

        <div class="progress-summary">
          <div>
            <span>任务总数</span>
            <strong>{{ rows.length }}</strong>
          </div>
          <div>
            <span>已完成</span>
            <strong class="status-completed">{{ completedCount }}</strong>
          </div>
          <div>
            <span>异常/失败</span>
            <strong class="status-error">{{ failedCount }}</strong>
          </div>
        </div>

        <div class="task-list">
          <div v-for="(row, index) in rows" :key="row.id" class="task-card">
            <div class="task-head">
              <div>
                <strong>任务 {{ index + 1 }}</strong>
                <span>{{ row.cardCode }}</span>
              </div>
              <span :class="getOrderStatusClass(row)">{{ getOrderStatusText(row) }}</span>
            </div>

            <div class="ios-progress-track task-progress">
              <div
                class="ios-progress-fill"
                :class="{
                  'is-completed': isCompleted(row),
                  'is-error': isFailed(row),
                  'is-returned': isReturned(row)
                }"
                :style="{ width: Math.round(row.progress) + '%' }"
              ></div>
            </div>

            <div class="task-grid">
              <div>
                <span>订单编号</span>
                <strong>{{ row.orderInfo && row.orderInfo.orderId || '-' }}</strong>
              </div>
              <div>
                <span>目标邮箱</span>
                <strong>{{ row.orderInfo && row.orderInfo.email || row.tokenInfo.email || '-' }}</strong>
              </div>
              <div>
                <span>商品名称</span>
                <strong>{{ row.cardInfo.productName }}</strong>
              </div>
              <div>
                <span>进度</span>
                <strong>{{ Math.round(row.progress) }}%</strong>
              </div>
            </div>

            <div v-if="row.submitError || row.pollError" class="task-message" :class="{ danger: isFailed(row) }">
              {{ row.submitError || row.pollError }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { createOrder, queryOrderByCard, validateCard } from '../services/api'
import {
  ORDER_STATUS,
  getOrderStatusClass,
  getOrderStatusText,
  normalizeOrderStatus
} from '../constants/orderStatus'
import { parseSessionToken } from '../utils/sessionToken'

const MAX_ROWS = 5
const PROGRESS_DURATION = 30000
const PROGRESS_INTERVAL = 200
const POLL_INTERVAL = 3000

export default {
  name: 'BatchRecharge',
  data() {
    return {
      step: 0,
      rows: [this.createRow()],
      validating: false,
      submitting: false,
      error: '',
      timers: {}
    }
  },
  computed: {
    maxRows() {
      return MAX_ROWS
    },
    canValidate() {
      return this.rows.length > 0 && this.rows.every((row) => row.cardCode.trim() && row.token.trim())
    },
    validCount() {
      return this.rows.filter((row) => row.validated).length
    },
    completedCount() {
      return this.rows.filter((row) => this.isCompleted(row)).length
    },
    failedCount() {
      return this.rows.filter((row) => this.isFailed(row) || this.isReturned(row)).length
    }
  },
  beforeDestroy() {
    this.stopAllTracking()
  },
  methods: {
    createRow() {
      return {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        cardCode: '',
        token: '',
        cardInfo: null,
        tokenInfo: null,
        cardError: '',
        tokenError: '',
        validated: false,
        validating: false,
        submitting: false,
        submitError: '',
        pollError: '',
        orderInfo: null,
        progress: 0,
        progressStartedAt: 0,
        orderStatus: ORDER_STATUS.PENDING
      }
    },
    addRow() {
      if (this.rows.length >= MAX_ROWS) return
      this.rows.push(this.createRow())
      this.error = ''
    },
    removeRow(index) {
      this.rows.splice(index, 1)
      this.error = ''
    },
    clearRowValidation(row) {
      row.cardInfo = null
      row.tokenInfo = null
      row.cardError = ''
      row.tokenError = ''
      row.validated = false
      this.error = ''
    },
    validateLocalInput() {
      this.error = ''

      if (this.rows.length > MAX_ROWS) {
        this.error = `批量模式最多添加 ${MAX_ROWS} 条任务`
        return false
      }

      const seenCards = new Set()
      let ok = true

      this.rows.forEach((row) => {
        row.cardError = ''
        row.tokenError = ''
        row.validated = false

        if (!row.cardCode.trim()) {
          row.cardError = '请输入卡密'
          ok = false
        } else if (seenCards.has(row.cardCode.trim())) {
          row.cardError = '卡密重复'
          ok = false
        } else {
          seenCards.add(row.cardCode.trim())
        }

        if (!row.token.trim()) {
          row.tokenError = '请输入 Token'
          ok = false
        }
      })

      return ok
    },
    async validateAll() {
      if (!this.validateLocalInput()) return

      this.validating = true
      this.error = ''

      await Promise.all(this.rows.map((row) => this.validateRow(row)))

      this.validating = false

      if (this.rows.every((row) => row.validated)) {
        this.step = 1
      } else {
        this.error = '部分任务校验未通过，请根据提示修改后重新校验'
      }
    },
    async validateRow(row) {
      row.validating = true
      row.cardInfo = null
      row.tokenInfo = null
      row.cardError = ''
      row.tokenError = ''
      row.validated = false

      const tokenResponse = parseSessionToken(row.token)
      const cardResponse = await validateCard(row.cardCode.trim())

      if (cardResponse.code === 200 && cardResponse.data && cardResponse.data.cardStatus === 1) {
        row.cardInfo = cardResponse.data
      } else {
        row.cardError = cardResponse.message || '卡密不可用'
      }

      if (tokenResponse.ok) {
        row.tokenInfo = tokenResponse.data
      } else {
        row.tokenError = tokenResponse.message
      }

      row.validated = !row.cardError && !row.tokenError
      row.validating = false
    },
    async submitAll() {
      this.submitting = true
      this.error = ''
      this.stopAllTracking()

      this.rows.forEach((row) => {
        row.submitting = true
        row.submitError = ''
        row.pollError = ''
        row.orderInfo = null
        row.progress = 0
        row.orderStatus = ORDER_STATUS.PENDING
      })

      this.step = 2
      await Promise.all(this.rows.map((row) => this.submitRow(row)))
      this.submitting = false
    },
    async submitRow(row) {
      const response = await createOrder(row.cardCode.trim(), row.token)

      row.submitting = false

      if (response.code === 200 && response.data) {
        row.orderInfo = response.data
        row.orderStatus = normalizeOrderStatus(response.data.status)
        row.progressStartedAt = Date.now()
        row.progress = this.isFinalStatus(row) ? 100 : 0

        if (!this.isFinalStatus(row)) {
          this.startTracking(row)
        }
      } else {
        row.submitError = response.message || '订单提交失败'
        row.orderStatus = ORDER_STATUS.EXCEPTION
        row.progress = 100
      }
    },
    startTracking(row) {
      this.stopTracking(row)
      const progressTimer = setInterval(() => {
        this.updateProgress(row)
      }, PROGRESS_INTERVAL)
      const pollTimer = setInterval(() => {
        this.pollRow(row)
      }, POLL_INTERVAL)

      this.$set(this.timers, row.id, { progressTimer, pollTimer })
      this.pollRow(row)
    },
    stopTracking(row) {
      const timer = this.timers[row.id]
      if (!timer) return

      clearInterval(timer.progressTimer)
      clearInterval(timer.pollTimer)
      this.$delete(this.timers, row.id)
    },
    stopAllTracking() {
      Object.keys(this.timers).forEach((id) => {
        clearInterval(this.timers[id].progressTimer)
        clearInterval(this.timers[id].pollTimer)
      })
      this.timers = {}
    },
    updateProgress(row) {
      if (this.isFinalStatus(row)) {
        row.progress = 100
        this.stopTracking(row)
        return
      }

      const elapsed = Date.now() - row.progressStartedAt
      row.progress = Math.min(99, (elapsed / PROGRESS_DURATION) * 99)
    },
    async pollRow(row) {
      if (this.isFinalStatus(row)) return

      const response = await queryOrderByCard(row.cardCode.trim())

      if (response.code === 200 && response.data) {
        row.pollError = ''
        row.orderInfo = {
          ...row.orderInfo,
          ...response.data
        }
        row.orderStatus = normalizeOrderStatus(row.orderInfo.status)

        if (this.isFinalStatus(row)) {
          row.progress = 100
          this.stopTracking(row)
        }
      } else if (response.data && response.data.found === false) {
        row.orderStatus = ORDER_STATUS.CARD_RETURNED
        row.pollError = response.data.message || response.message || '充值失败，卡密已退回'
        row.progress = 100
        this.stopTracking(row)
      } else {
        row.pollError = response.message || '订单结果查询失败，系统将继续重试'
      }
    },
    resetBatch() {
      this.stopAllTracking()
      this.step = 0
      this.rows = [this.createRow()]
      this.validating = false
      this.submitting = false
      this.error = ''
    },
    getValidateStatusText(row) {
      if (row.validating) return '校验中'
      if (row.validated) return '校验通过'
      if (row.cardError || row.tokenError) return '校验失败'
      return '待校验'
    },
    getValidateStatusClass(row) {
      if (row.validated) return 'status-available'
      if (row.cardError || row.tokenError) return 'status-error'
      return 'status-unknown'
    },
    isCompleted(row) {
      return normalizeOrderStatus(row.orderStatus) === ORDER_STATUS.COMPLETED
    },
    isReturned(row) {
      return normalizeOrderStatus(row.orderStatus) === ORDER_STATUS.CARD_RETURNED
    },
    isFailed(row) {
      return normalizeOrderStatus(row.orderStatus) === ORDER_STATUS.EXCEPTION
    },
    isFinalStatus(row) {
      return this.isCompleted(row) || this.isReturned(row) || this.isFailed(row)
    },
    getOrderStatusText(row) {
      if (row.submitting) return '提交中'
      if (row.submitError) return '提交失败'
      return getOrderStatusText(row.orderStatus)
    },
    getOrderStatusClass(row) {
      if (row.submitError) return 'status-error'
      return getOrderStatusClass(row.orderStatus)
    }
  }
}
</script>

<style scoped>
.batch-panel {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.batch-head,
.batch-item-head,
.task-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.batch-title-wrap {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.batch-icon {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: rgba(0, 122, 255, 0.1);
  color: var(--ios-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.batch-icon svg {
  width: 21px;
  height: 21px;
}

.panel-title {
  font-size: 19px;
  font-weight: 600;
}

.panel-desc {
  margin-top: 3px;
  font-size: 14px;
  color: var(--ios-label-secondary);
}

.compact-button {
  min-height: 36px;
  padding: 0 14px;
  border-radius: 11px;
  flex-shrink: 0;
}

.compact-button:disabled {
  color: var(--ios-label-tertiary);
  background: var(--ios-fill);
  cursor: not-allowed;
}

.batch-list,
.task-list {
  display: flex;
  flex-direction: column;
}

.batch-item,
.task-card {
  padding: 16px 0;
  border-radius: 0;
  background: transparent;
  position: relative;
}

.batch-item + .batch-item,
.task-card + .task-card {
  border-top: 1px solid var(--ios-separator);
}

.batch-item.has-error {
  background: transparent;
}

.batch-item-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  font-weight: 600;
}

.task-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.task-title span {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--ios-fill);
  color: var(--ios-label-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.task-title strong {
  font-size: 15px;
  font-weight: 600;
}

.field-grid {
  display: grid;
  grid-template-columns: minmax(180px, 0.85fr) minmax(0, 1.15fr);
  gap: 14px;
  margin-top: 12px;
}

.input-group {
  min-width: 0;
}

.token-input {
  min-height: 78px;
}

.batch-panel .ios-input,
.batch-panel .ios-textarea {
  padding: 12px 14px;
  font-size: 15px;
  border-radius: 11px;
}

.field-success {
  margin-top: 8px;
  font-size: 13px;
  color: var(--ios-green);
  word-break: break-all;
}

.link-button {
  border: none;
  background: transparent;
  color: var(--ios-blue);
  font: inherit;
  cursor: pointer;
}

.link-button.danger {
  color: var(--ios-red);
}

.link-button:disabled {
  color: var(--ios-label-tertiary);
  cursor: not-allowed;
}

.button-group {
  display: flex;
  gap: 12px;
}

.button-group .btn-gray,
.button-group .btn-filled {
  flex: 1;
}

.confirm-list {
  background: transparent;
  border-radius: 12px;
  border: 1px solid var(--ios-separator);
}

.confirm-row {
  align-items: flex-start;
}

.confirm-row div {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.confirm-index,
.confirm-row small {
  font-size: 12px;
  color: var(--ios-label-secondary);
  word-break: break-all;
}

.progress-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.progress-summary div {
  padding: 10px 12px;
  border-radius: 11px;
  background: rgba(118, 118, 128, 0.08);
}

.progress-summary span,
.task-grid span {
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--ios-label-secondary);
}

.progress-summary strong {
  font-size: 20px;
  line-height: 1;
}

.task-head > div {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.task-head span {
  font-size: 13px;
  color: var(--ios-label-secondary);
  word-break: break-all;
}

.task-head > span {
  flex-shrink: 0;
  font-weight: 700;
}

.task-progress {
  margin-top: 10px;
}

.ios-progress-fill.is-completed {
  background: var(--ios-green);
}

.ios-progress-fill.is-error {
  background: var(--ios-red);
}

.ios-progress-fill.is-returned {
  background: var(--ios-orange);
}

.task-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
  margin-top: 10px;
}

.task-grid strong {
  display: block;
  font-size: 14px;
  word-break: break-all;
}

.task-message {
  margin-top: 10px;
  padding: 9px 11px;
  border-radius: 10px;
  background: rgba(255, 149, 0, 0.12);
  color: #8a5300;
  font-size: 13px;
  line-height: 1.45;
}

.task-message.danger {
  background: rgba(255, 59, 48, 0.08);
  color: #c0271d;
}

@media (max-width: 600px) {
  .batch-panel {
    padding: 16px 14px;
  }

  .batch-head,
  .field-grid,
  .button-group {
    align-items: stretch;
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .batch-title-wrap {
    align-items: flex-start;
  }

  .compact-button {
    width: 100%;
  }

  .progress-summary,
  .task-grid {
    grid-template-columns: 1fr;
  }
}
</style>
